import { useCallback, useEffect, useRef, useState } from "react";
import API_BASE, {
  deleteCurrentAccount,
  clearCsrfToken,
  setCsrfToken,
  toggleFavoriteSpot,
  withApiCredentials,
} from "../api";
import {
  clearAnalyticsSession,
  endAnalyticsSession,
} from "../analytics/sessionTracking";
import {
  clearAllBootstrapTokens,
  clearBootstrapTokenForSource,
  getActiveAwareAuthToken,
  getAuthBootstrapTokenCandidates,
  setActiveAuthTokenSource,
  setAuthTransitionBlocked,
  shouldDiscardBootstrapTokenAfterValidationFailure,
} from "../auth/bootstrapTokens";
import { clearOAuthCompletionToken } from "../auth/oauthFlow";
import { clearSupabaseBrowserSession } from "../auth/supabaseClient";
import {
  mergeUserDirectoryPreservingNewerUsernames,
  mergeUserPreservingNewerUsername,
} from "../auth/userFreshness";
import {
  reportBackgroundRefreshBlocked,
  shouldBlockBackgroundRefresh,
} from "./catchSubmitGate";

function roundFavoriteSpotCoordinate(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Number(number.toFixed(6));
}

function areSameFavoriteSpot(left, right) {
  const leftLat = roundFavoriteSpotCoordinate(left?.lat);
  const leftLng = roundFavoriteSpotCoordinate(left?.lng);
  const rightLat = roundFavoriteSpotCoordinate(right?.lat);
  const rightLng = roundFavoriteSpotCoordinate(right?.lng);

  return (
    leftLat !== null &&
    leftLng !== null &&
    leftLat === rightLat &&
    leftLng === rightLng
  );
}

function hydrateCsrfToken(payload) {
  if (typeof payload?.csrfToken === "string" && payload.csrfToken.trim()) {
    setCsrfToken(payload.csrfToken);
  }
}

function withoutCsrfToken(payload) {
  if (!payload || typeof payload !== "object" || !("csrfToken" in payload)) {
    return payload;
  }
  const { csrfToken, ...rest } = payload;
  return rest;
}

export default function useAppSessionState({
  activeTab,
  setActiveTab,
  setShowMenu,
  pushLifecycleRef = null,
}) {
  const [isBootstrappingStoredSession] = useState(() => {
    if (typeof window === "undefined") return false;
    return true;
  });
  const [user, setUser] = useState(null);
  const [authResolved, setAuthResolved] = useState(() => {
    if (typeof window === "undefined") return false;
    return false;
  });
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [profileBioEditRequest, setProfileBioEditRequest] = useState(null);
  const profileBioEditRequestSequenceRef = useRef(0);
  const [profileFocusPostRequest, setProfileFocusPostRequest] = useState(null);
  const [userProfileFocusPostRequest, setUserProfileFocusPostRequest] =
    useState(null);
  const [favoriteSpots, setFavoriteSpots] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [groupSearchResults, setGroupSearchResults] = useState([]);
  const [openGroupSearchRequest, setOpenGroupSearchRequest] = useState(null);
  const [isAuthTransitioning, setIsAuthTransitioning] = useState(false);

  const requestProfileBioEdit = useCallback(() => {
    profileBioEditRequestSequenceRef.current += 1;
    setProfileBioEditRequest({
      id: profileBioEditRequestSequenceRef.current,
    });
  }, []);

  const consumeProfileBioEditRequest = useCallback((requestId) => {
    setProfileBioEditRequest((currentRequest) =>
      currentRequest?.id === requestId ? null : currentRequest
    );
  }, []);

  useEffect(() => {
    setFavoriteSpots(Array.isArray(user?.favoriteSpots) ? user.favoriteSpots : []);
  }, [user]);

  useEffect(() => {
    const handleFavoriteSpotHabitatUpdated = (event) => {
      const updatedSpot = event?.detail?.favoriteSpot;
      if (!updatedSpot?.spotKey) return;
      setFavoriteSpots((current) =>
        current.map((spot) =>
          spot.spotKey === updatedSpot.spotKey ? updatedSpot : spot
        )
      );
      setUser((current) =>
        current
          ? {
              ...current,
              favoriteSpots: (current.favoriteSpots || []).map((spot) =>
                spot.spotKey === updatedSpot.spotKey ? updatedSpot : spot
              ),
            }
          : current
      );
    };

    window.addEventListener(
      "favoriteSpotHabitatUpdated",
      handleFavoriteSpotHabitatUpdated
    );
    return () =>
      window.removeEventListener(
        "favoriteSpotHabitatUpdated",
        handleFavoriteSpotHabitatUpdated
      );
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadUser() {
      const tokenCandidates = getAuthBootstrapTokenCandidates(window);
      if (tokenCandidates.length === 0) {
        setActiveAuthTokenSource(null, window);
        const cookieSessionResponse = await fetch(
          `${API_BASE}/auth/me`,
          withApiCredentials()
        );

        if (cookieSessionResponse.ok) {
          const cookieSessionPayload = await cookieSessionResponse.json();
          hydrateCsrfToken(cookieSessionPayload);
          if (isMounted) {
            setUser((currentUser) =>
              mergeUserPreservingNewerUsername(
                currentUser,
                withoutCsrfToken(cookieSessionPayload)
              )
            );
          }
        } else if (
          cookieSessionResponse.status >= 500 ||
          cookieSessionResponse.status === 0
        ) {
          console.warn("Cookie session bootstrap failed.");
        } else if (isMounted) {
          setUser(null);
        }

        if (isMounted) setAuthResolved(true);
        return;
      }

      let restoredUser = null;
      let legacyRestoreFailed = false;

      for (const candidate of tokenCandidates) {
        const res = await fetch(`${API_BASE}/auth/me`, withApiCredentials({
          headers: {
            Authorization: `Bearer ${candidate.token}`,
          },
        }));

        if (res.ok) {
          const restoredPayload = await res.json();
          hydrateCsrfToken(restoredPayload);
          restoredUser = withoutCsrfToken(restoredPayload);
          setActiveAuthTokenSource(candidate.source, window);
          break;
        }

        if (
          candidate.source === "legacy" &&
          shouldDiscardBootstrapTokenAfterValidationFailure(res.status)
        ) {
          legacyRestoreFailed = true;
        }

        if (shouldDiscardBootstrapTokenAfterValidationFailure(res.status)) {
          clearBootstrapTokenForSource(candidate.source, window);
        }
      }

      if (!restoredUser && legacyRestoreFailed) {
        clearBootstrapTokenForSource("legacy", window);
      }

      if (!restoredUser) {
        setActiveAuthTokenSource(null, window);
      }

      try {
        if (restoredUser && isMounted) {
          setUser((currentUser) =>
            mergeUserPreservingNewerUsername(currentUser, restoredUser)
          );
          return;
        }

        if (isMounted) {
          setUser(null);
        }
      } catch (err) {
        console.error("Auth error:", err);
      } finally {
        if (isMounted) {
          setAuthResolved(true);
        }
      }
    }

    loadUser().catch((err) => {
      console.error("Auth error:", err);
      if (isMounted) {
        setUser(null);
        setAuthResolved(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    async function loadUsers() {
      try {
        if (shouldBlockBackgroundRefresh()) {
          reportBackgroundRefreshBlocked({ source: "auth_users_search" });
          return;
        }
        const res = await fetch(`${API_BASE}/auth/users`);
        if (!res.ok) return;
        const data = await res.json();
        setSearchResults((currentUsers) =>
          mergeUserDirectoryPreservingNewerUsernames(currentUsers, data || [])
        );
      } catch (err) {
        console.error("Failed to load users for search:", err);
      }
    }

    loadUsers();
  }, [user?.id]);

  useEffect(() => {
    if (!user) {
      setGroupSearchResults([]);
      return;
    }

    if (activeTab !== "groups") {
      setGroupSearchResults([]);
    }
  }, [activeTab, user]);

  useEffect(() => {
    const handleUserUpdated = (event) => {
      const updatedUser = event.detail;
      setUser((currentUser) =>
        mergeUserPreservingNewerUsername(currentUser, updatedUser)
      );
      if (!updatedUser?.id) return;
      const replaceUser = (items) =>
        items.map((item) =>
          item?.id === updatedUser.id
            ? mergeUserPreservingNewerUsername(item, updatedUser)
            : item
        );
      setSearchResults(replaceUser);
      setGroupSearchResults(replaceUser);
    };

    window.addEventListener("userUpdated", handleUserUpdated);

    return () => {
      window.removeEventListener("userUpdated", handleUserUpdated);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function refreshUserAfterFriendsUpdate() {
      if (shouldBlockBackgroundRefresh()) {
        reportBackgroundRefreshBlocked({ source: "auth_me_friends_update" });
        return;
      }
      const tokenCandidates = getAuthBootstrapTokenCandidates(window);
      if (tokenCandidates.length === 0) {
        const res = await fetch(`${API_BASE}/auth/me`, withApiCredentials());

        if (res.ok) {
          const data = await res.json();
          hydrateCsrfToken(data);
          if (isMounted) {
            setUser((currentUser) =>
              mergeUserPreservingNewerUsername(
                currentUser,
                withoutCsrfToken(data)
              )
            );
          }
        }
        return;
      }

      for (const candidate of tokenCandidates) {
        const res = await fetch(`${API_BASE}/auth/me`, withApiCredentials({
          headers: {
            Authorization: `Bearer ${candidate.token}`,
          },
        }));

        if (res.ok) {
          const data = await res.json();
          hydrateCsrfToken(data);
          setActiveAuthTokenSource(candidate.source, window);
          if (isMounted) {
            setUser((currentUser) =>
              mergeUserPreservingNewerUsername(
                currentUser,
                withoutCsrfToken(data)
              )
            );
          }
          return;
        }

        if (shouldDiscardBootstrapTokenAfterValidationFailure(res.status)) {
          clearBootstrapTokenForSource(candidate.source, window);
        }
      }
    }

    function handleFriendsUpdated() {
      refreshUserAfterFriendsUpdate().catch((err) => {
        console.error("Failed to refresh user after friends update:", err);
      });
    }

    window.addEventListener("friendsUpdated", handleFriendsUpdated);

    return () => {
      isMounted = false;
      window.removeEventListener("friendsUpdated", handleFriendsUpdated);
    };
  }, []);

  const handleAddFavoriteSpot = async (spot) => {
    const token = getActiveAwareAuthToken(window);
    if (!spot) return false;

    const previousFavoriteSpots = favoriteSpots;
    const alreadyFavorited = previousFavoriteSpots.some((savedSpot) =>
      areSameFavoriteSpot(savedSpot, spot)
    );
    const nextFavoriteSpots = alreadyFavorited
      ? previousFavoriteSpots.filter(
          (savedSpot) => !areSameFavoriteSpot(savedSpot, spot)
        )
      : [...previousFavoriteSpots, spot];

    setFavoriteSpots(nextFavoriteSpots);
    setUser((prevUser) =>
      prevUser
        ? {
            ...prevUser,
            favoriteSpots: nextFavoriteSpots,
          }
        : prevUser
    );

    try {
      const updatedUser = await toggleFavoriteSpot(spot, token);
      setUser((currentUser) =>
        mergeUserPreservingNewerUsername(currentUser, updatedUser)
      );
      return true;
    } catch (err) {
      console.error("Failed to update favorite spot:", err);
      setFavoriteSpots(previousFavoriteSpots);
      setUser((prevUser) =>
        prevUser
          ? {
              ...prevUser,
              favoriteSpots: previousFavoriteSpots,
            }
          : prevUser
      );
      return false;
    }
  };

  const resetSessionAfterSignOut = () => {
    endAnalyticsSession();
    clearAnalyticsSession();
    clearAllBootstrapTokens(window);
    clearCsrfToken();
    clearOAuthCompletionToken();
    clearSupabaseBrowserSession({ logWarning: true });
    setIsAuthTransitioning(false);
    setUser(null);
    setShowProfile(false);
    setShowUserProfile(false);
    setShowMenu(false);
  };

  const requestBackendLogout = async () => {
    try {
      await fetch(
        `${API_BASE}/auth/logout`,
        withApiCredentials({ method: "POST" })
      );
    } catch (error) {
      console.warn("Backend logout did not complete.", error);
    }
  };

  const handleLogout = async () => {
    setIsAuthTransitioning(true);
    setAuthTransitionBlocked(true, window);
    try {
      await pushLifecycleRef?.current?.prepareForLogout?.();
    } catch {
      // Push cleanup is best-effort and must never prevent logout.
    }
    await requestBackendLogout();
    resetSessionAfterSignOut();
  };

  const handleDeleteAccount = async () => {
    const token = getActiveAwareAuthToken(window);
    if (!token && !user?.id) {
      throw new Error("You are no longer signed in.");
    }

    setIsAuthTransitioning(true);
    setAuthTransitionBlocked(true, window);

    try {
      const result = await deleteCurrentAccount(token || "");
      try {
        await pushLifecycleRef?.current?.cleanupAfterAccountDeletion?.();
      } catch {
        // The server deletion is authoritative; local cleanup is best-effort.
      }
      resetSessionAfterSignOut();
      if (result?.authCleanupDeferred) {
        console.warn(
          "Account delete completed but Supabase auth cleanup still needs follow-up."
        );
      }
      return result;
    } catch (error) {
      setAuthTransitionBlocked(false, window);
      setIsAuthTransitioning(false);
      throw error;
    }
  };

  const handleOpenUserFromSearch = (username) => {
    if (!username) return;

    setSearchQuery("");
    setShowProfile(false);
    setShowUserProfile(false);

    if (username === user?.username) {
      setSelectedUser(null);
      setShowProfile(true);
    } else {
      setSelectedUser(username);
      setShowUserProfile(true);
    }
  };

  const handleOpenGroupFromSearch = (group) => {
    if (!group?.id) return;

    setSearchQuery("");
    setShowProfile(false);
    setShowUserProfile(false);
    setSelectedUser(null);
    setActiveTab("groups");
    setOpenGroupSearchRequest({
      groupId: group.id,
      nonce: Date.now(),
    });
  };

  const handleLegalAcceptance = (acceptedUser) => {
    setUser((currentUser) =>
      mergeUserPreservingNewerUsername(currentUser, acceptedUser)
    );
  };

  return {
    user,
    setUser,
    authResolved,
    isBootstrappingStoredSession,
    isAuthTransitioning,
    profileUi: {
      showUserProfile,
      setShowUserProfile,
      selectedUser,
      setSelectedUser,
      showProfile,
      setShowProfile,
      profileBioEditRequest,
      requestProfileBioEdit,
      consumeProfileBioEditRequest,
      profileFocusPostRequest,
      setProfileFocusPostRequest,
      userProfileFocusPostRequest,
      setUserProfileFocusPostRequest,
    },
    favorites: {
      favoriteSpots,
      showFavorites,
      setShowFavorites,
      handleAddFavoriteSpot,
    },
    search: {
      searchQuery,
      setSearchQuery,
      searchResults,
      groupSearchResults,
      setGroupSearchResults,
      openGroupSearchRequest,
    },
    actions: {
      handleDeleteAccount,
      handleLogout,
      handleOpenUserFromSearch,
      handleOpenGroupFromSearch,
      handleLegalAcceptance,
    },
  };
}
